import React, { useEffect, useState } from "react";
import { Check, Loader2, PackageCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { L } from "@/components/ideaos/shared";

/** Seller-side guided delivery: tick the materials/steps, then close the transfer with one tap. */
export function DeliveryChecklist({ txId, onDone }) {
  const { lang } = useLang();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/transactions/${txId}/delivery`, { params: { lang } }).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
  useEffect(() => { load(); }, [txId, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="h-24 rounded-2xl bg-slate-50 animate-pulse" data-testid="delivery-loading" />;
  if (d.error) return null;
  const toggle = async (key) => {
    const checked = d.items.filter((x) => (x.key === key ? !x.done : x.done)).map((x) => x.key);
    setBusy(true);
    try { const { data } = await api.patch(`/transactions/${txId}/delivery`, { checked }, { params: { lang } }); setD(data); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const complete = async () => {
    setBusy(true);
    try { await api.post(`/transactions/${txId}/transfer/complete`); toast.success(L(lang, "Trasferimento completato", "Transfer completed")); await load(); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const done = d.items.filter((x) => x.done).length;
  const completed = d.transfer_status === "completed";
  return (
    <div className="rounded-2xl bg-[#f4f6fa] p-4" data-testid={`delivery-${txId}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold text-[#0b0f1a] inline-flex items-center gap-2"><Truck size={14} className="text-[#7c3aed]" /> {L(lang, "Consegna guidata", "Guided delivery")} · {done}/{d.items.length}</p>
        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${completed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="delivery-status">
          {completed ? L(lang, "Trasferito", "Transferred") : L(lang, "Pagato · in consegna", "Paid · delivering")}
        </span>
      </div>
      <ul className="mt-3 space-y-1.5">
        {d.items.map((x) => (
          <li key={x.key}>
            <button type="button" disabled={busy || completed} onClick={() => toggle(x.key)} aria-pressed={x.done} data-testid={`delivery-item-${x.key}`}
              className="flex w-full items-center gap-3 rounded-xl bg-white px-3 py-2 text-left text-xs ring-1 ring-slate-100 disabled:opacity-70">
              <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${x.done ? "border-[#0b0f1a] bg-[#0b0f1a] text-white" : "border-slate-300"}`}>{x.done && <Check size={12} />}</span>
              <span className={x.done ? "text-slate-400 line-through" : "text-[#0b0f1a]"}>{x.label}</span>
              {x.kind === "material" && <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-400">{L(lang, "materiale", "material")}</span>}
            </button>
          </li>
        ))}
      </ul>
      {!completed && (
        <button type="button" disabled={!d.can_complete || busy} onClick={complete} data-testid="delivery-complete"
          className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#0b0f1a] text-xs font-semibold text-white disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />} {L(lang, "Conferma consegna e chiudi il trasferimento", "Confirm delivery and close the transfer")}
        </button>
      )}
      {!completed && d.status !== "completed" && <p className="mt-2 text-[11px] text-slate-500">{L(lang, "Il pagamento non è ancora confermato dal sistema.", "Payment not yet confirmed by the payment system.")}</p>}
    </div>
  );
}
